import { MenuItem, TextField } from '@mui/material'
import { Dispatch, SetStateAction, useContext } from 'react'

import { AppStateContext } from '../../contexts'
import { Bank, BankAccount, Customer } from '../../types'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { handleGetParsedValue, numberWithCommas } from '../../utils'

export const ColombianCustomerPaymentForm = ({
  errors,
  customerData,
  amountToSend,
  selectedCurrency,
  showBankNamesData,
  setShowBankNamesData,
  customersDocumentType,
  handleSetCustomerData,
  availableBankAccountTypes,
  handleSetPaymentAmountToSend
}: {
  amountToSend: string
  selectedCurrency: string
  showBankNamesData: boolean
  errors?: { [key: string]: string }
  customerData: Partial<Customer> | null
  customersDocumentType: { [key: string]: string }
  availableBankAccountTypes?: { [key: string]: string }
  setShowBankNamesData: Dispatch<SetStateAction<boolean>>
  handleSetPaymentAmountToSend: (paymentAmountToSend: string) => void
  handleSetCustomerData: (attributeToSet: string, valueToSet: string | Array<BankAccount>) => void
}) => {
  const { banksData } = useContext(AppStateContext)

  const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = e.target.value

    if (input === '') {
      return ''
    }

    const first = input[0]

    if (/[0-9]/.test(first)) {
      if (/^\d+$/.test(input)) {
        return input
      }
    }

    if (first === '@') {
      const pattern = /^@[A-Za-z0-9]*$/
      if (pattern.test(input)) {
        return input
      }
    }

    if (/[A-Za-z]/.test(first)) {
      const allowedChars = /^[A-Za-z0-9._%+\-@]*$/
      if (!allowedChars.test(input)) {
        return
      }

      const atCount = (input.match(/@/g) || []).length
      if (atCount > 1) {
        return
      }

      return input
    }
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px', padding: '20px 0px', boxSizing: 'border-box', borderTop: '1px solid #DDD' }}>
      <div className="customers-add-new-form-personal-data-title">Datos del pago</div>
      <div className="customers-add-new-form-personal-data-container">
        <div className="customers-add-new-form-personal-data-form-row">
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              label="Nombre(s)"
              error={!!errors?.firstName}
              helperText={errors?.firstName}
              value={customerData?.firstName || ''}
              onChange={(event) => {
                handleSetCustomerData('firstName', event.target.value.toUpperCase())
              }}
            />
          </div>
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              label="Apellido(s)"
              error={!!errors?.lastName}
              helperText={errors?.lastName}
              value={customerData?.lastName || ''}
              onChange={(event) => {
                handleSetCustomerData('lastName', event.target.value.toUpperCase())
              }}
            />
          </div>
        </div>
        <div className="customers-add-new-form-personal-data-form-row">
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              select
              fullWidth
              variant="standard"
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
              inputProps={{
                style: { textAlign: 'center' }
              }}
              label="Tipo de documento"
              error={!!errors?.documentType}
              helperText={errors?.documentType}
              value={customerData?.documentType || ''}
              onChange={(event) => {
                handleSetCustomerData('documentType', event.target.value.toUpperCase())
              }}
            >
              <MenuItem
                disabled
                value=""
              >
                Selecciona
              </MenuItem>
              {Object.keys(customersDocumentType).map((documentType: string) => {
                return (
                  <MenuItem
                    key={documentType}
                    value={documentType}
                  >
                    {(customersDocumentType as any)[documentType].toUpperCase()}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              label="Número de documento"
              error={!!errors?.documentNumber}
              helperText={errors?.documentNumber}
              value={customerData?.documentNumber || ''}
              onChange={(event) => {
                handleSetCustomerData('documentNumber', event.target.value.toUpperCase())
              }}
            />
          </div>
        </div>
      </div>
      <div className="customers-add-new-form-bank-data-container">
        <div className="customers-add-new-form-bank-data-title">Ingresa los datos bancarios de la cuenta principal</div>
        <div className="customers-add-new-form-personal-data-form-row">
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              select
              fullWidth
              variant="standard"
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
              inputProps={{
                style: { textAlign: 'center' }
              }}
              label="Tipo de cuenta"
              error={!!errors?.bankAccountType}
              helperText={errors?.bankAccountType}
              value={Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0 ? customerData?.bankAccountsData[0].bankAccountType : ''}
              onChange={(event) => {
                const currentCustomerBankAccountsData: Array<BankAccount> =
                  Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0
                    ? [...customerData?.bankAccountsData]
                    : [{ bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: true }]
                currentCustomerBankAccountsData[0].bankAccountType = event.target.value

                handleSetCustomerData('bankAccountsData', [currentCustomerBankAccountsData[0]])

                if (event.target.value === 'breb') {
                  setShowBankNamesData(false)
                } else {
                  setShowBankNamesData(true)
                }
              }}
            >
              <MenuItem
                disabled
                value=""
              >
                Selecciona
              </MenuItem>
              {Object.keys(availableBankAccountTypes as { [key: string]: string }).map((bankAccountType: string) => {
                return (
                  <MenuItem
                    key={bankAccountType}
                    value={bankAccountType}
                  >
                    {(availableBankAccountTypes as any)[bankAccountType].toUpperCase()}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
          {showBankNamesData && (
            <div className="customers-add-new-form-personal-data-form-field-container">
              <TextField
                select
                fullWidth
                variant="standard"
                sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
                inputProps={{
                  style: { textAlign: 'center' }
                }}
                label="Banco"
                error={!!errors?.bankName}
                helperText={errors?.bankName}
                value={
                  customerData?.bankAccountsData &&
                  Array.isArray(customerData?.bankAccountsData) &&
                  customerData?.bankAccountsData.length > 0 &&
                  customerData?.bankAccountsData[0].bankName &&
                  customerData?.bankAccountsData[0].bankAchCode
                    ? `${customerData?.bankAccountsData[0].bankName}-${customerData?.bankAccountsData[0].bankAchCode}`
                    : ''
                }
                onChange={(event) => {
                  const currentCustomerBankAccountsData: Array<BankAccount> =
                    Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0
                      ? [...customerData?.bankAccountsData]
                      : [{ bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: true }]
                  currentCustomerBankAccountsData[0].bankName = event.target.value.split('-')[0]
                  currentCustomerBankAccountsData[0].bankAchCode = event.target.value.split('-')[1]

                  handleSetCustomerData('bankAccountsData', [currentCustomerBankAccountsData[0]])
                }}
              >
                <MenuItem
                  disabled
                  value=""
                >
                  Selecciona
                </MenuItem>
                {banksData?.map((bankData: Bank) => {
                  return (
                    <MenuItem
                      key={bankData._id}
                      value={`${bankData.name}-${bankData.achCode}`}
                    >
                      {bankData.name.toUpperCase()}
                    </MenuItem>
                  )
                })}
              </TextField>
            </div>
          )}
        </div>
        <div className="customers-add-new-form-personal-data-form-row">
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              label="Número de cuenta o llave"
              error={!!errors?.bankAccountNumber}
              helperText={errors?.bankAccountNumber}
              value={Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0 ? customerData?.bankAccountsData[0].bankNumber : ''}
              onChange={(event) => {
                // const validPattern = /^(@[A-Za-z0-9]*|\d*)$/

                // if (!validPattern.test(event.target.value)) return
                const valueToSet = handleAccountNumberChange(event)

                const currentCustomerBankAccountsData: Array<BankAccount> =
                  Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0
                    ? [...customerData?.bankAccountsData]
                    : [{ bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: true }]
                // currentCustomerBankAccountsData[0].bankNumber = event.target.value
                currentCustomerBankAccountsData[0].bankNumber = valueToSet ? String(valueToSet) : ''

                handleSetCustomerData('bankAccountsData', [currentCustomerBankAccountsData[0]])
              }}
            />
          </div>
        </div>
        {handleSetPaymentAmountToSend && (
          <div className="customers-add-new-form-personal-data-form-row">
            <div className="customers-add-new-form-personal-data-form-field-container">
              <TextField
                fullWidth
                variant="standard"
                sx={textFieldWhiteBg}
                value={amountToSend}
                error={!!errors?.amountToSend}
                helperText={errors?.amountToSend}
                label={`Monto a enviar ($${selectedCurrency})`}
                onBlur={(event) => {
                  if (event.target.value === '') {
                    handleSetPaymentAmountToSend(`$ ${numberWithCommas(Number(amountToSend))}`)
                  }
                }}
                onChange={(event) => {
                  const extractedNumber = event.target.value[0] === '$' ? Number(event.target.value.slice(1).trim().replaceAll(',', '')) : Number(event.target.value.trim().replaceAll(',', ''))

                  if (isNaN(extractedNumber)) {
                    return
                  }

                  if (event.target.value.trim() === '$' || event.target.value.trim() === '0') {
                    return handleSetPaymentAmountToSend('')
                  }

                  const valueToSet = handleGetParsedValue(event)
                  handleSetPaymentAmountToSend(valueToSet)
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
